import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { get } from "lodash";
import groupBy from "lodash/groupBy";
import { useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import api from "../api/api";
import { useTypedSelector } from "../hooks/useTypedSelector";
import useUrlQuery from "../hooks/useUrlQuery";
import { addOrRemoveRider, setSelectedRiders } from "../modules/quotes.slice";
import { useGetHealthProductByIdQuery } from "./Landing.service";
import { useGetProposalDetailsQuery } from "./Proposal.service";
import Swal from "sweetalert2";
import { toast } from "react-toastify";

export interface GetQuotesResponseInterface {
  status: 200 | 500;
  PREMIUM_DATA: {
    PREMIUM_DATA: {
      NET_PREMIUM: string;
      TOTAL_PREMIUM: string;
      COVER_AMOUNT: string;
      BLOCK_PORTING: any;
      PORTING_MSG: string;
    };
  };
  PRODUCT_DATA: {
    PRODUCTID: number;
    IC_ID: number;
    ADULT_MIN: number;
    ADULT_MAX: number;
    LOB: string;
    POLICY_TYPE: string;
    PRODUCT_TYPE: string;
    PRODUCT_NAME: string;
    PRODUCT_IDENTIFIER: string;
    CREATED_DATE: string;
    UPDATED_DATE: string;
    INSURANCE_NAME: string;
    INSURANCE_SLUG: string;
    ADDRESS: string;
    CONTACT_DETAILS: string;
    CLAIM_FORM_LINK: string;
    BROCHURE_LINK: string;
    LOGO_PATH: string;
    CLAIM_SETTLEMENT_RATIO: string;
    PRIORITY: number;
  };
  PRODUCT_FEATURES: {
    ID: number;
    FEATURE_ID: number;
    PRODUCT_ID: number;
    FEATURE_VAL: string;
    CREATED_AT: string;
    HIGHLIGHT: number;
    USP_TEXT: string;
    FEATURE_TITLE: string;
    FEATURE_DESCRIPTION: string;
  }[];
}
export const useGetQuotes = () => {
  const { quote_end_points } = useGetHealthProductByIdQuery();
  const { filters } = useTypedSelector((state) => state.quote);
  const { preferred_insurer } = filters;
  const isInsurerFilterActive = preferred_insurer.length > 0;

  // Explicitly type quote_end_points as a Record<string, string[]>
  const typed_quote_end_points: Record<string, string[]> = quote_end_points as unknown as Record<string, string[]>;

  const quote_end_points_array = Object.keys(typed_quote_end_points)
    .map((key) =>
      typed_quote_end_points[key].map((endpoint: string) => ({
        alias: key,
        endpoint,
      }))
    )
    .flat() as { alias: string; endpoint: string }[];

  const first_batch = Object.keys(typed_quote_end_points)
    .map((key) => typed_quote_end_points[key][0])
    .flat();

  const firstBatchQueries = first_batch.map((endpoint) => {
    // Find the alias for this endpoint
    const alias = Object.keys(typed_quote_end_points).find((key) =>
      typed_quote_end_points[key].includes(endpoint)
    );
    return {
      queryKey: ["quote", endpoint],
      queryFn: () => {
        return api.get<GetQuotesResponseInterface>(endpoint);
      },
      placeholderData: {
        data: {
          status: 200,
          PREMIUM_DATA: {
            PREMIUM_DATA: {
              TOTAL_PREMIUM: "0",
              COVER_AMOUNT: "0",
            },
          },
          PRODUCT_DATA: {
            INSURANCE_SLUG: alias,
          },
        } as GetQuotesResponseInterface,
      },
    };
  });
  const firstBatchResults = useQueries({
    // @ts-ignore
    queries: firstBatchQueries,
  });
  const isAllFetched = firstBatchResults
    .map((item) => item.isFetched)
    .every((item) => item === true);
  const queries = quote_end_points_array.map((item) => ({
    queryKey: ["quote", item.endpoint],
    queryFn: () => {
      return api.get<GetQuotesResponseInterface>(item.endpoint);
    },
    enabled:
      first_batch.some((batch) => batch === item.endpoint) ||
      isAllFetched,
    placeholderData: {
      data: {
        status: 200,
        PREMIUM_DATA: {
          PREMIUM_DATA: {
            TOTAL_PREMIUM: "0",
            COVER_AMOUNT: "0",
          },
        },
        PRODUCT_DATA: {
          INSURANCE_SLUG: item.alias,
        },
      } as GetQuotesResponseInterface,
    },
  }));

  const results = useQueries({
    // @ts-ignore
    queries: queries,
  });
  const fetchedQuotes = results
    .filter(
      (result) =>
        result?.data?.data?.PREMIUM_DATA &&
        result.data?.data?.status !== 500 &&
        !result.error &&
        !result.isPlaceholderData
    )
    .map((result) => ({
      quoteData: result.data?.data,
      isLoading: result.isFetching,
      isPaused: result.isPaused || result.isPlaceholderData,
    }))
    .sort((a, b) => {
      if (a.isLoading && !b.isPaused) {
        return 1;
      } else if (b.isLoading && !a.isPaused) {
        return -1;
      }
      return (
        Number(a.quoteData?.PRODUCT_DATA?.PRIORITY) -
        Number(b.quoteData?.PRODUCT_DATA?.PRIORITY)
      );
    });
  const uniqueSortedQuotesSet = new Set(
    fetchedQuotes
      .sort((a, b) => {
        if (a.isLoading) {
          return 1;
        } else if (b.isLoading) {
          return -1;
        }
        return (
          Number(a.quoteData?.PRODUCT_DATA?.PRIORITY) -
          Number(b.quoteData?.PRODUCT_DATA?.PRIORITY)
        );
      })
      .map((item) => item.quoteData?.PRODUCT_DATA.INSURANCE_SLUG)
  );
  const uniqueSortedQuotesKeys = Array.from(uniqueSortedQuotesSet);
  const companiesWithLogos = fetchedQuotes.reduce((acc, item) => {
    if (item.quoteData?.PRODUCT_DATA.INSURANCE_SLUG) {
      acc[item.quoteData?.PRODUCT_DATA.INSURANCE_SLUG] = {
        logo: item.quoteData?.PRODUCT_DATA.LOGO_PATH,
        name: item.quoteData?.PRODUCT_DATA.INSURANCE_NAME,
        value: item.quoteData?.PRODUCT_DATA.INSURANCE_SLUG,
      };
    }
    return acc;
  }, {} as { [key: string]: { logo: string; name: string; value: string } });

  const totalQuotes = fetchedQuotes.length;
  const progress =
    totalQuotes - fetchedQuotes.filter((quote) => quote.isLoading).length;

  const progressPercentage = (progress / totalQuotes) * 100;
  return {
    fetchedQuotes,
    groupedQuotes: groupBy(
      fetchedQuotes.filter((quote) => {
        if (isInsurerFilterActive) {
          return preferred_insurer.includes(
            quote.quoteData?.PRODUCT_DATA?.INSURANCE_SLUG || ""
          );
        }
        return true;
      }),
      (item: any) =>
        item.quoteData?.PRODUCT_DATA?.INSURANCE_SLUG?.split("_")
          .slice(0, 2)
          .join("_")
    ),
    quoteLength: fetchedQuotes.filter((item) => !item.isLoading).length,

    progressPercentage,
    companiesWithLogos,
  };
};

export const useGetMultiYearQuotes = () => {
  const { quote_data, isProposalLoading, product } =
    useGetProposalDetailsQuery();

  const { getUrlQuery } = useUrlQuery();
  const proposal_id = getUrlQuery("proposal_id");

  const { data, isLoading } = useQuery({
    queryKey: [
      "multiyear",
      quote_data?.COMPANY_SLUG,
      quote_data?.SUM_INSURED,
      product?.IC_ID,
      proposal_id,
    ],

    queryFn: () =>
      api.get(
        `api/GetMultiYearHealthQuotes/${quote_data?.COMPANY_SLUG}/${quote_data?.PRODUCT_ID}/${quote_data?.QUOTE_ID}/${quote_data?.SUM_INSURED}/0`
      ),

    enabled:
      !isProposalLoading &&
      !!quote_data?.COMPANY_SLUG &&
      !!quote_data?.PRODUCT_ID &&
      !!quote_data?.QUOTE_ID,
  });

  const premiumData = data?.data?.PREMIUM_DATA ?? {};

  return {
    quote_data,

    discounts: Object.values(premiumData),

    isAnyDiscounts: Object.values(premiumData).length > 0,

    isDiscountsLoading: isLoading,
  };
};

export interface RiderInterface {
  PREMIUM_DATA: any;
  addon_name: string;
  premium: string;
  ADD_ON_ID: number;
  PRODUCT_ID: number;
  ADD_ON_NAME: string;
  ADD_ON_SLUG: string;
  IS_SI_REQUIRED: "Y" | "N";
  ALLOWED_SI: "string" | null;
  IS_MANDETORY: "Y" | "N";
  IS_MEMBER_LEVEL: "Y" | "N";
  SUM_INSURED: string;
  VALUE: string;
  DISABLE_ADDON: string;
  selectedMembers: string[];
  ENABLE_ADDON?: string;
  CHECK_ENABLED?: boolean;
  DISABLE_ADDON_SLUG: string;
  ADDON_DESCRIPTION: string;
  DROPDOWN_TITLE: string;
  additionalOptions: any;
}
interface GetRidersInterface {
  status: 200 | 500;
  data: {
    PORTING_MSG: any;
    ADDON_DATA: RiderInterface[];
    PREMIUM_DATA?: GetQuotesResponseInterface["PREMIUM_DATA"]["PREMIUM_DATA"];
    BLOCK_PORTING: any;
  };
  showpopup?: boolean;
  message?: string;
  port_flag?: boolean;
}
export const useGetRiders = (
  selectedMembers: any,
  quote_data: any,
  isProposalLoading: boolean
) => {
  const { getUrlQuery } = useUrlQuery();
  const proposal_id = getUrlQuery("proposal_id");
  const selectedRiders = useTypedSelector(
    (state) => state.quote.selectedRiders
  );

  const { selectedTenure } = useTypedSelector((state) => state.quote);

  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const port_flag = quote_data?.port_flag
    ? quote_data?.port_flag
    : quote_data?.PORT_POLICY;

  const canFetchRiders =
    !isProposalLoading &&
    !!proposal_id &&
    !!quote_data?.SUM_INSURED &&
    !!selectedTenure;

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: canFetchRiders
      ? [
        "riders",
        proposal_id,
        selectedTenure,
        quote_data?.SUM_INSURED,
        JSON.stringify(selectedMembers),
        port_flag,
      ]
      : [],
    queryFn: () =>
      api.post<GetRidersInterface>("api/GetAddonDetails", {
        PROPOSAL_ID: proposal_id,
        TENURE: selectedTenure,
        SUM_INSURED: quote_data?.SUM_INSURED,
        SELECTED_ADDONS:
          selectedRiders?.length
            ? selectedRiders
            : quote_data?.ADD_ONS || [],
        selectedMembers:
          selectedMembers && Object.keys(selectedMembers).length > 0
            ? selectedMembers
            : quote_data?.selected_members || {},
        port_flag,
      }),
    enabled: canFetchRiders,
    retry: false,
    retryOnMount: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const datas = data?.data;

  /**
   * Handle Addon Data
   */
  useEffect(() => {
    if (!data) return;

    if (data?.data?.status === 500) {
      toast.error(data?.data?.message);
      return;
    }

    const addonData = data?.data.data.ADDON_DATA || [];

    const mandatoryAddons = addonData.filter(
      (item: any) => item.IS_MANDETORY === "Y"
    );

    const selectedMandatoryAddons = selectedRiders.filter(
      (item: any) => item.IS_MANDETORY === "Y"
    );

    const selectedNonMandatoryAddons = selectedRiders.filter(
      (item: any) => item.IS_MANDETORY === "N"
    );

    const mergedMandatory = [
      ...selectedMandatoryAddons,
      ...mandatoryAddons.filter(
        (addon: any) =>
          !selectedMandatoryAddons.some(
            (sel: any) => sel.ADD_ON_ID === addon.ADD_ON_ID
          )
      ),
    ];

    const finalSelectedRiders = [
      ...mergedMandatory,
      ...selectedNonMandatoryAddons,
    ];

    const changed =
      finalSelectedRiders.length !== selectedRiders.length ||
      finalSelectedRiders.some(
        (r: any, i: number) =>
          r.ADD_ON_ID !== selectedRiders[i]?.ADD_ON_ID ||
          r.premium !== selectedRiders[i]?.premium
      );

    if (changed) {
      dispatch(setSelectedRiders(finalSelectedRiders));
    }

    if (data?.data?.showpopup) {
      Swal.fire("Alert", data?.data?.message);
    }
  }, [data]);

  /**
   * Update Proposal Premium
   */
  useEffect(() => {
    const newRiders = data?.data?.data?.ADDON_DATA;

    if (!newRiders || !selectedRiders.length) return;

    const ridersWithUpdatedPremium = selectedRiders.map((rider: any) => {
      const newRider = newRiders.find(
        (r: any) => r?.ADD_ON_ID === rider?.ADD_ON_ID
      );

      if (newRider) {
        return {
          ...rider,
          premium: newRider.premium,
        };
      }

      return rider;
    });

    if (
      JSON.stringify(ridersWithUpdatedPremium) !==
      JSON.stringify(selectedRiders)
    ) {
      dispatch(setSelectedRiders(ridersWithUpdatedPremium));
    }
  }, [data?.data?.data?.ADDON_DATA]);

  /**
   * Update Premium in Proposal Cache
   */
  useEffect(() => {
    if (data?.data?.data?.PREMIUM_DATA) {
      queryClient.setQueryData(["proposal", proposal_id], (old_data: any) => {
        if (!old_data?.data?.data?.PROPOSAL) return old_data;

        return {
          ...old_data,
          data: {
            ...old_data.data,
            data: {
              ...old_data.data.data,
              PROPOSAL: {
                ...old_data.data.data.PROPOSAL,
                TOTAL_PREMIUM:
                  data?.data?.data?.PREMIUM_DATA?.TOTAL_PREMIUM,
                NET_PREMIUM:
                  data?.data?.data?.PREMIUM_DATA?.TOTAL_PREMIUM,
                BLOCK_PORTING: quote_data?.ISPORTING,
              },
            },
          },
        };
      });
    }
  }, [data?.data?.data?.PREMIUM_DATA]);

  /**
   * Riders Enable/Disable Logic
   */
  let riders_to_disable: any = [];
  let riders_to_enable: any = [];
  let riders_to_disable_slug: any = [];

  data?.data?.data?.ADDON_DATA?.forEach((item: any) => {
    if (
      item.DISABLE_ADDON &&
      selectedRiders.some((rider: any) => rider.ADD_ON_ID === item.ADD_ON_ID)
    ) {
      riders_to_disable = [
        ...riders_to_disable,
        ...String(item.DISABLE_ADDON).split(","),
      ];
    }

    if (
      item.DISABLE_ADDON_SLUG &&
      selectedRiders.some(
        (rider: any) => rider.ADD_ON_SLUG === item.ADD_ON_SLUG
      )
    ) {
      riders_to_disable_slug.push(String(item.DISABLE_ADDON_SLUG));
    }

    if (
      item.ENABLE_ADDON &&
      selectedRiders.some((rider: any) => rider.ADD_ON_ID === item.ADD_ON_ID)
    ) {
      riders_to_enable = [
        ...riders_to_enable,
        ...String(item.ENABLE_ADDON).split(","),
      ];
    }
  });

  return {
    riders: data?.data?.data?.ADDON_DATA || [],
    riders_to_disable,
    riders_to_enable,
    riders_to_disable_slug,
    isRidersLoading: isLoading,
    refetchRiders: refetch,
    isFetching,
    port_flag,
    data,
  };
};
// http://localhost:3000/product-summary?proposal_id=69be5bc88082a81e99026cd2&session_token=4OzwAt8wp7H9S07NLE9whArcnEo4w6m8HCrJr6HJ
export interface IFeature {
  FEATURE_ID: number;
  CATEGORY_ID: number;
  FEATURE_TITLE: string;
  FEATURE_DESCRIPTION: string;
  CREATED_AT: string;
  ID: number;
  PRODUCT_ID: number;
  FEATURE_VAL: string;
  HIGHLIGHT: number;
  USP_TEXT: null;
  TYPE: string;
}
export const useGetViewPlanDetails = (product_id: number | undefined) => {
  const { data, isLoading } = useQuery({
    queryKey: ["view_plan", product_id],
    queryFn: () =>
      api.get(`api/GetHealthProductFeature?product_id=${product_id}`),
    enabled: !!product_id,
  });

  return {
    viewPlanDetails: data?.data,
    highlights: data?.data?.data?.highlights as IFeature[] | undefined,
    value_added_services: data?.data?.data?.[3]?.features as
      | IFeature[]
      | undefined,
    additionalFeatures: data?.data?.data?.[2]?.features as
      | IFeature[]
      | undefined,
    waiting_periods: data?.data?.data?.[1]?.features as IFeature[] | undefined,
    coverage: data?.data?.data?.[0]?.features as IFeature[] | undefined,
    documents: data?.data?.data?.[4]?.features as IFeature[] | undefined,
    isPlanDetailsLoading: isLoading,
  };
};

export const useGetQuote = (endpoint: string) => {
  const { data, isLoading } = useQuery({
    queryKey: ["quote", endpoint],

    queryFn: () =>
      api.get<GetQuotesResponseInterface>(endpoint),

    enabled: !!endpoint,
  });

  return {
    product_data: data?.data?.PRODUCT_DATA,
    premium_data: data?.data?.PREMIUM_DATA?.PREMIUM_DATA,
    isLoading,
  };
};